import { buildWebinarCampaignSeed } from "@xop/dashboard-shared";
import MailIcon from "@mui/icons-material/Mail";
import { Alert, Box, Button, Stack } from "@mui/material";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";

import { useCan } from "../../../../auth/permissions.js";
import {
  CampaignStatus,
  DoneNote,
  OpenCampaignButton,
  SafetyNote,
  StepFooter,
  StepIntro,
  type StepProps,
} from "../stepShared.js";

/**
 * Step 1 — the invitation email.
 *
 * This step does NOT build an email. It hands the operator to the Email
 * Campaigns editor they already use, with crm-api's rich `webinar` layout
 * selected and every detail they typed on the webinar (title, date, length,
 * join link, description) already filled in.
 *
 * That is a deliberate choice over generating the HTML here: the webinar
 * email already has TWO renderers (the in-browser section builder and
 * crm-api's server-side fallback), and a third would drift from both. The
 * editor auto-saves a draft, and links itself back to this webinar the moment
 * it has an id — see `email-campaign-form.tsx`'s webinar seam.
 */
export function StepInvite({ detail, canEdit, markDone }: StepProps): JSX.Element {
  const navigate = useNavigate();
  const { webinar, campaigns } = detail;
  // The campaign editor runs on the CRM runtime; without `crm:view` the
  // dashboard cannot even mint a CRM session, so the tab is not there.
  const canReachCampaigns = useCan("crm:view");
  const campaignId = webinar.invite_campaign_id;

  const openEditor = (): void => {
    navigate("/marketing/campaigns/create", {
      state: { webinarSeed: buildWebinarCampaignSeed(webinar) },
    });
  };

  if (!canReachCampaigns) {
    return (
      <Alert severity="warning">
        Building the invitation needs access to Email Campaigns. Ask an administrator for the
        CRM view permission, then come back to this step.
      </Alert>
    );
  }

  return (
    <Box>
      <StepIntro>
        Make the invitation email and choose who gets it. We open the normal email editor with
        the webinar layout and all of this webinar&apos;s details already filled in.
      </StepIntro>

      {campaignId ? (
        <Stack spacing={2}>
          <DoneNote>The invitation email exists.</DoneNote>
          <CampaignStatus summary={campaigns.invite} />
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <OpenCampaignButton campaignId={campaignId} label="Open the invitation" />
            {canEdit && (
              <Button variant="text" onClick={openEditor}>
                Start a different one
              </Button>
            )}
          </Box>
          <SafetyNote />
        </Stack>
      ) : (
        <Stack spacing={2} alignItems="flex-start">
          <Button
            variant="contained"
            size="large"
            startIcon={<MailIcon />}
            onClick={openEditor}
            disabled={!canEdit}
            data-testid="build-invite"
          >
            Build the invitation email
          </Button>
          <SafetyNote />
        </Stack>
      )}

      <StepFooter
        step="invite"
        detail={detail}
        markDone={markDone}
        canEdit={canEdit}
        {...(campaignId
          ? {}
          : { disabledReason: "Build the invitation first, then mark this step done." })}
      />
    </Box>
  );
}
